import { useMutation, useQueryClient } from "@tanstack/react-query";
import GlobalAppAPI from "../../api/GlobalAppAPI";
import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { useAtomValue, useSetAtom } from "jotai";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { AppConfirm } from "../../../utils/PopupUtil";
import { setSchemaAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";

interface Props {
  globalAppID: string;
  deleteEverything: boolean;
  onSuccess?: () => void;
}

/**
 * For uninstalling GLOBAL apps
 * Cloud Admins ONLY!
 */
const useUninstallGlobalApp = () => {
  const client = useQueryClient();
  const setSchema = useSetAtom(setSchemaAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const { cPermissions } = useCloudPermissions();

  const uninstallGlobalApp = useMutation({
    mutationFn: async ({ globalAppID, deleteEverything }: Props) => {
      if (!cPermissions.cloud_modify_permissions) {
        return Promise.reject(
          "You don't have permission to perform this operation!"
        );
      }

      newToastEvent(
        "success",
        "Uninstalling global app...",
        "UninstallingGlobalApp",
        {
          loading: true,
          autoClose: false,
        }
      );

      return GlobalAppAPI.uninstallGlobalApp(
        globalAppID,
        workspaceID as string,
        deleteEverything
      );
    },
    onSuccess: async (data, { onSuccess }) => {
      const schema = data?.data?.data?.schema;
      schema && setSchema(schema);

      await client.refetchQueries({ queryKey: ["workspace"] });
      await client.refetchQueries({ queryKey: ["cloudApps", tenantID] });

      AppConfirm({
        title: "Uninstall Complete",
        icon: "success",
        html: `Successfully uninstalled Global App.`,
        confirmButtonText: "OK",
        confirmButtonColor: "var(--primary)",
      });

      updateToastEvent(
        "success",
        `Successfully uninstalled Global App!`,
        "UninstallingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );

      callFunctionIfExists(onSuccess);
    },
    onError: (error) => {
      AppConfirm({
        title: "Error",
        html: `Failed to uninstall app! <br/> ${getErrorMessage(error)}`,
        confirmButtonText: "OK",
      });

      updateToastEvent(
        "error",
        `Failed to uninstall app! ${getErrorMessage(error)}`,
        "UninstallingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );
    },
  });

  return {
    uninstallGlobalApp: uninstallGlobalApp.mutate,
    uninstallingApp: uninstallGlobalApp.isPending,
  };
};

export default useUninstallGlobalApp;
